import { useQuery } from "react-query";

import { getData } from "../../../../newApis/apiMethods";
import * as ajaxEndpoints from "../../../../api/ajax-endpoints";
import { IPaginatedResponse } from "../../../../interfaces/response";

// Mock data for development
const USE_MOCK = false;

export const mockBlackListData: BlackListEntry[] = [
  {
    id: 1,
    customerType: "Individual",
    firstName: "John",
    lastName: "Doe",
    businessName: null,
    bvn: "22334455667",
    nin: "12345678901",
    rcNumber: null,
    email: "john.doe@example.com",
    phoneNumber: "+2348012345678",
    isActive: true,
    activationReason: "Fraudulent activities detected in multiple transactions",
    deactivationReason: null,
    createdAt: "2024-10-15T10:30:00",
    createdBy: 101,
    deactivatedAt: null,
    deactivatedBy: null,
    createdByName: "Admin User",
    deactivatedByName: null,
  },
  {
    id: 2,
    customerType: "Organisation",
    firstName: null,
    lastName: null,
    businessName: "ABC Limited",
    bvn: null,
    nin: null,
    rcNumber: "RC123456",
    email: "contact@abclimited.com",
    phoneNumber: "+2348098765432",
    isActive: true,
    activationReason:
      "Repeated loan defaults and failure to respond to communication",
    deactivationReason: null,
    createdAt: "2024-09-28T14:20:00",
    createdBy: 102,
    deactivatedAt: null,
    deactivatedBy: null,
    createdByName: "Credit Manager",
    deactivatedByName: null,
  },
  {
    id: 3,
    customerType: "Organisation",
    firstName: null,
    lastName: null,
    businessName: "Techify Global Solutions Ltd",
    bvn: null,
    nin: null,
    rcNumber: "RC987635",
    email: "customerservice@techify.com",
    phoneNumber: "+2348098778898",
    isActive: false,
    activationReason:
      "Company registered under multiple fraudulent business names.",
    deactivationReason: "Issue resolved after further investigation",
    createdAt: "2025-10-31T04:41:08",
    createdBy: 2444,
    deactivatedAt: "2025-11-01T10:15:00",
    deactivatedBy: 2445,
    createdByName: "Nora Don",
    deactivatedByName: "Jane Smith",
  },
];

export type TBlackListParams = {
  pageNumber: number;
  pageSize: number;
  startDate?: string;
  endDate?: string;
  phoneNumber?: string;
  email?: string;
  rcNumber?: string;
  bvn?: string;
  nin?: string;
  customerType?: string;
};

export type TParams = {
  params: TBlackListParams;
  options?: {
    enabled?: boolean;
  };
};

export interface BlackListEntry {
  id: number;
  customerType: string;
  firstName: string | null;
  lastName: string | null;
  businessName: string | null;
  bvn: string | null;
  nin: string | null;
  rcNumber: string | null;
  email: string;
  phoneNumber: string;
  isActive: boolean;
  activationReason: string | null;
  deactivationReason: string | null;
  createdAt: string;
  createdBy: number;
  deactivatedAt: string | null;
  deactivatedBy: number | null;
  createdByName: string;
  deactivatedByName: string | null;
}

export const getBlackList = async ({
  params: {
    pageNumber,
    pageSize,
    startDate,
    endDate,
    phoneNumber,
    email,
    rcNumber,
    bvn,
    nin,
    customerType,
  },
}: TParams): Promise<IPaginatedResponse<BlackListEntry[]>> => {
  if (USE_MOCK) {
    // Return mock data using setTimeout to simulate network delay
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          message: "BlackList records retrieved successfully",
          page: pageNumber,
          pageSize: pageSize,
          status: true,
          status_code: 200,
          totalCount: 50,
          totalPages: Math.ceil(50 / pageSize),
          data: mockBlackListData,
        });
      }, 1000);
    });
  }

  let queryParams = `?pageNumber=${pageNumber}&pageSize=${pageSize}`;
  if (startDate) queryParams += `&startDate=${startDate}`;
  if (endDate) queryParams += `&endDate=${endDate}`;
  if (phoneNumber) queryParams += `&phoneNumber=${phoneNumber}`;
  if (email) queryParams += `&email=${email}`;
  if (rcNumber) queryParams += `&rcNumber=${rcNumber}`;
  if (bvn) queryParams += `&bvn=${bvn}`;
  if (nin) queryParams += `&nin=${nin}`;
  if (customerType) queryParams += `&customerType=${customerType}`;

  const data = await getData(`${ajaxEndpoints.DB_BLACKLIST}${queryParams}`);

  return data;
};

export const useGetBlackList = ({ params, options }: TParams) => {
  return useQuery<IPaginatedResponse<BlackListEntry[]>, Error>(
    ["blackList", params],
    () => getBlackList({ params, options }),
    {
      enabled: options?.enabled ?? true,
      keepPreviousData: true,
    }
  );
};
